import { Record } from 'immutable';
import { handleActions } from 'redux-actions';

import type { QcsSyncResourceStoreProps } from 'app-components/flux/core/resourceStoreTypes';

import { resourceUndoUpserters, resourceUpserters } from '../core';
import * as actions from './preferencesV2Actions';
import {
  mkPreferencesV2Store,
  mkPreferenceV2,
  type PreferencesV2Store,
  type PreferenceV2Props,
} from './preferencesV2Types';
import { isValidJSON } from './preferencesV2Utils';

const preferencesV2Reducer = handleActions<
  PreferencesV2Store,
  | ReturnType<typeof actions.getPreferencesV2>
  | ReturnType<typeof actions.getPreferencesV2Success>
  | ReturnType<typeof actions.getPreferencesV2Failure>
  | ReturnType<typeof actions.getSpecificPreferenceV2Success>
  | ReturnType<typeof actions.getSpecificPreferenceV2Failure>
  | ReturnType<typeof actions.updatePreferenceOverride>
  | ReturnType<typeof actions.updatePreferenceOverrideFailure>
  | ReturnType<typeof actions.applyPreferenceChanges>
  | ReturnType<typeof actions.resetPreferenceOverride>
>(
  {
    [actions.getPreferencesV2.type]: (state) => state.merge({ isLoading: true }),
    [actions.getPreferencesV2Success.type]: (state, { payload }) => {
      const revisedPayload =
        'merge' in payload && typeof payload.merge === 'function'
          ? payload.merge({ doMerge: false })
          : { ...payload, doMerge: false };
      return resourceUpserters.upsertResources(state, revisedPayload);
    },
    [actions.getPreferencesV2Failure.type]: (state, { payload: error }) =>
      resourceUpserters.completeWithError<PreferenceV2Props, QcsSyncResourceStoreProps<PreferenceV2Props>>(
        state,
        error,
      ),

    [actions.getSpecificPreferenceV2Success.type]: (state, { payload }) => {
      const revisedPayload =
        'merge' in payload && typeof payload.merge === 'function'
          ? payload.merge({ doMerge: false })
          : { ...payload, doMerge: false };
      return resourceUpserters.upsertResources(state, revisedPayload);
    },
    [actions.getSpecificPreferenceV2Failure.type]: (state, { payload: error }) =>
      resourceUpserters.completeWithError<PreferenceV2Props, QcsSyncResourceStoreProps<PreferenceV2Props>>(
        state,
        error,
      ),

    [actions.updatePreferenceOverride.type]: (state, action) => {
      const { payload, meta } = action;
      if (payload?.dataType && (payload?.dataType === 'JSON' || payload?.dataType === 'ARRAY')) {
        if (!assert(isValidJSON(payload.value), 'invalid pref JSON value', payload.value)) {
          return state;
        }
      }
      const resource = Record.isRecord(payload) ? payload : mkPreferenceV2(payload);
      if (meta?.undo) {
        return resourceUndoUpserters.upsertResourceSetupUndo<
          PreferenceV2Props,
          QcsSyncResourceStoreProps<PreferenceV2Props>
        >(state, { ...action, payload: resource }, actions.updatePreferenceOverride);
      }
      return resourceUpserters.upsertResource<PreferenceV2Props, QcsSyncResourceStoreProps<PreferenceV2Props>>(
        state,
        resource,
        false,
      );
    },
    [actions.updatePreferenceOverrideFailure.type]: (state, { payload: error }) =>
      resourceUpserters.completeWithError<PreferenceV2Props, QcsSyncResourceStoreProps<PreferenceV2Props>>(
        state,
        error,
      ),

    [actions.applyPreferenceChanges.type]: (state, { payload }) => resourceUpserters.upsertResources(state, payload),

    [actions.resetPreferenceOverride.type]: (state, { payload }) => {
      if (payload.defaultValue) {
        const alteredPayload = mkPreferenceV2({ ...payload, value: payload.defaultValue });
        return resourceUpserters.upsertResource<PreferenceV2Props, QcsSyncResourceStoreProps<PreferenceV2Props>>(
          state,
          alteredPayload,
        );
      }
      return state;
    },
  },
  mkPreferencesV2Store(),
);

export default preferencesV2Reducer;
